import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

const BUCKET_NAME = 'logo-assets';

const FAVICON_FILES = [
  { fileName: 'favicon.ico', rel: 'icon', type: 'image/x-icon', sizes: null },
  { fileName: 'favicon-32x32.png', rel: 'icon', type: 'image/png', sizes: '32x32' },
  { fileName: 'favicon-16x16.png', rel: 'icon', type: 'image/png', sizes: '16x16' },
  { fileName: 'apple-touch-icon.png', rel: 'apple-touch-icon', type: 'image/png', sizes: '180x180' },
  { fileName: 'icon-192x192.png', rel: 'icon', type: 'image/png', sizes: '192x192' },
  { fileName: 'icon-512x512.png', rel: 'icon', type: 'image/png', sizes: '512x512' },
];

const updateFaviconLink = (rel, type, sizes, href) => {
  let selector = `link[rel="${rel}"]`;
  if (sizes) selector += `[sizes="${sizes}"]`;
  else if (type) selector += `[type="${type}"]`;

  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (type) link.type = type;
    if (sizes) link.sizes = sizes;
    document.head?.appendChild(link);
  }
  link.href = href + '?t=' + Date.now();
};

const patchManifestWithDynamicIcons = (iconUrls) => {
  try {
    // Remove existing manifest link
    const existingManifest = document.querySelector('link[rel="manifest"]');
    
    // Fetch current manifest and patch icons
    fetch('/manifest.json')?.then(r => r?.json())?.then(manifest => {
        const patchedIcons = [...(manifest?.icons || [])];
        
        // Override/add dynamic icon entries
        iconUrls?.forEach(({ url, sizes, type }) => {
          const idx = patchedIcons?.findIndex(i => i?.sizes === sizes);
          const entry = { src: url, sizes, type, purpose: 'any maskable' };
          if (idx >= 0) {
            patchedIcons[idx] = entry;
          } else {
            patchedIcons?.push(entry);
          }
        });

        const patchedManifest = { ...manifest, icons: patchedIcons };
        const blob = new Blob([JSON.stringify(patchedManifest)], { type: 'application/json' });
        const blobUrl = URL.createObjectURL(blob);

        if (existingManifest) {
          existingManifest.href = blobUrl;
        } else {
          let link = document.createElement('link');
          link.rel = 'manifest';
          link.href = blobUrl;
          document.head?.appendChild(link);
        }
      })?.catch(() => {});
  } catch (err) {
    // Silently fail
  }
};

const useDynamicFavicon = () => {
  useEffect(() => {
    const applyFavicons = async () => {
      try {
        const { data: files, error } = await supabase?.storage
          ?.from(BUCKET_NAME)
          ?.list('logos', { limit: 50 });

        if (error || !files?.length) return;

        const uploadedFileNames = new Set(files.map(f => f?.name));
        const manifestIconUpdates = [];

        for (const config of FAVICON_FILES) {
          if (!uploadedFileNames?.has(config?.fileName)) continue;

          const { data: urlData } = supabase?.storage
            ?.from(BUCKET_NAME)
            ?.getPublicUrl(`logos/${config?.fileName}`);

          if (!urlData?.publicUrl) continue;

          const urlWithBust = urlData?.publicUrl + '?t=' + Date.now();

          updateFaviconLink(config?.rel, config?.type, config?.sizes, urlData?.publicUrl);

          // Collect large icons for manifest patching
          if (config?.sizes === '192x192' || config?.sizes === '512x512' || config?.sizes === '180x180') {
            manifestIconUpdates?.push({
              url: urlWithBust,
              sizes: config?.sizes,
              type: config?.type,
            });
          }
        }

        // If we have large icons from storage, patch the manifest so bookmarks use them
        if (manifestIconUpdates?.length > 0) {
          patchManifestWithDynamicIcons(manifestIconUpdates);
        }
      } catch (err) {
        // Silently fail — fallback to static favicons in index.html
      }
    };

    applyFavicons();
  }, []);
};

export default useDynamicFavicon;
